import { computed, ref } from 'vue'
import { Notify } from 'quasar'
import { tabOutlineAlertCircle, tabOutlineCheck } from 'quasar-extras-svg-icons/tabler-icons-v3'
import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import type { Operation } from 'fast-json-patch'
import { applyPatch } from 'fast-json-patch'

import type { BasicDigitalTwin, BasicDigitalTwinComponent } from 'models/BasicDigitalTwin'
import type { BasicRelationship } from 'models/BasicRelationship'
import type { DigitalTwinsFile } from 'models/DigitalTwinsFile'
import type { FeatureParameterValues } from 'models/FeatureParameter'
import type {
  BatchFile,
  BatchStatus,
  BatchOrchestrationResult
} from 'services/TwinsBatchApiService'
import type { QueryResponseData } from 'utils/query-adt'
import { REL_TYPE_INCOMING, REL_TYPE_OUTGOING } from 'services/constants'
import { twinsApiService } from 'services/TwinsApiService'
import { generateQueryWithParameters } from 'utils/query-adt'
import { twinsBatchApiService } from 'services/TwinsBatchApiService'
import { twinsJobsApiService } from 'services/TwinsJobsApiService'
import type { ImportJob, ImportJobWithStatus } from 'services/TwinsJobsApiService'
import { runBatchPromises } from 'utils/batch'
import { isDigitalTwin } from 'utils/type-checks'
import { escapeCharsTwinId } from 'utils/twins'
import { useModelsStore, processModels } from './models'

export interface DigitalTwinsState {
  twins: { [twinId: string]: BasicDigitalTwin }
  relationships: {
    [twinId: string]: {
      [REL_TYPE_INCOMING]: BasicRelationship[]
      [REL_TYPE_OUTGOING]: BasicRelationship[]
    }
  }
}

/** Maximum number of upstream paths kept per twin (protects DAG fan-out). */
const MAX_TWIN_PATHS = 10

export const useDigitalTwinsStore = defineStore('digital-twins', () => {
  const twins = ref<DigitalTwinsState['twins']>({})
  const relationships = ref<DigitalTwinsState['relationships']>({})

  /** A list of all twins in the store */
  const allTwins = computed(() => Object.values(twins.value))

  /** A list of all rels in the store */
  const allRels = computed(() =>
    Object.values(relationships.value).reduce(
      (acc, twinRels) => [...acc, ...twinRels[REL_TYPE_OUTGOING]],
      [] as BasicRelationship[]
    )
  )

  /** Retrieve twins by providing their ids */
  function twinsByIds(ids: string[]) {
    return ids.map<BasicDigitalTwin | undefined>((id) => twins.value[id])
  }

  /** Retrieves twins by their relationship with the provided twin ids */
  function twinsByRelationship(
    twinIds: string | string[],
    relationshipName?: string,
    relType: typeof REL_TYPE_OUTGOING | typeof REL_TYPE_INCOMING = REL_TYPE_OUTGOING
  ): BasicDigitalTwin[] {
    const _twinIds = Array.isArray(twinIds) ? twinIds : [twinIds]
    // The key in the relationship that holds the twin id of the twin that bneeds to be added to the response
    const outputKey = relType === REL_TYPE_OUTGOING ? '$targetId' : '$sourceId'
    return _twinIds.reduce<BasicDigitalTwin[]>((acc, twinId) => {
      // Get relationships for the twinId and the specified type (incoming or outgoing) and optionally filter on relationship name
      const rels =
        relationships.value[twinId]?.[relType]?.filter(
          (rel) => !relationshipName || rel.$relationshipName === relationshipName
        ) || []
      // Get twins from store and filter undefineds and duplicates
      const related = rels
        .map((rel) => twins.value[rel[outputKey]])
        .filter(
          (value, index, self) =>
            // filter out duplicates
            self.indexOf(value) === index &&
            value?.$dtId &&
            !acc.some((x) => x.$dtId === value.$dtId)
        )
      // Merge new twins with existing list
      return [...acc, ...related]
    }, [])
  }

  /** Get incoming or outgoing related twins (and relationships) by provided twin */
  function relatedTwins(
    twinIds: string | string[],
    relationshipName?: string,
    relTypeLoading: typeof REL_TYPE_OUTGOING | typeof REL_TYPE_INCOMING = REL_TYPE_OUTGOING
  ) {
    const twins = twinsByRelationship(twinIds, undefined, relTypeLoading)
    const _twinIds = Array.isArray(twinIds) ? twinIds : [twinIds]
    const rels = _twinIds
      .map(
        (twinId) =>
          relationships.value[twinId]?.[relTypeLoading]?.filter(
            (rel) => !relationshipName || rel.$relationshipName === relationshipName
          ) || []
      )
      .flat()
    return { twins, relationships: rels }
  }

  /** Retrieves twins by their model */
  function twinsByModel(modelIds: string | string[], strict = false): BasicDigitalTwin[] {
    const _modelIds = Array.isArray(modelIds) ? modelIds : [modelIds]
    const allModelIds = [..._modelIds, ...(strict ? [] : useModelsStore().idsByBaseIds(_modelIds))]
    return allTwins.value.filter((twin: { $metadata: { $model: string } }) =>
      allModelIds.includes(twin?.$metadata?.$model)
    )
  }

  /** Returns all upstream paths from the provided twin id */
  function pathsById(id: string): BasicDigitalTwin[][] {
    const twinId = id
    if (!twinId) return []
    const baseTwin = twins.value[twinId]
    if (!baseTwin) return []
    const paths = [[baseTwin]]
    const onPath = (pathIdx: number, pathId: string): boolean =>
      paths[pathIdx].some((twin) => twin.$dtId === pathId)
    const addParentsPath = (parentTwinId: string, pathIdx: number) => {
      const parents = twinsByRelationship(parentTwinId, undefined, REL_TYPE_INCOMING)
      // state.twins.filter(x => state.relationships.filter(rel => rel.$targetId === twinId).some(y => y.$sourceId === x.$dtId))
      if (parents.length > 0) {
        for (let i = 1; i < parents.length; i++) {
          // Stop forking once enough alternates are known (DAG fan-out guard).
          if (paths.length >= MAX_TWIN_PATHS) break
          // Cycle guard: only simple paths (no repeated twins).
          if (onPath(pathIdx, parents[i].$dtId)) continue
          // Forked paths stay root-first like the main path: the alternate
          // parent goes to the front, ahead of the already resolved chain.
          const pathsLength = paths.push([parents[i], ...paths[pathIdx]])
          addParentsPath(parents[i].$dtId, pathsLength - 1)
        }
        const first = parents[0]
        if (!onPath(pathIdx, first.$dtId)) {
          paths[pathIdx].unshift(first) // add to current path
          addParentsPath(first.$dtId, pathIdx)
        }
      }
    }
    addParentsPath(twinId, 0)
    return paths
  }

  /**
   * Query the twin graph
   * @param query The ADT query (optionally with SQL style parameters)
   * @param parameterValues The parameter values used to replace in the query
   * @param relReplaceOptions Options to define whether relationships should be replaced
   */
  async function queryTwins(
    query: string,
    parameterValues?: FeatureParameterValues,
    relReplaceOptions?: {
      twinIds: string | string[]
      relationshipName?: string
      relTypeLoading?: typeof REL_TYPE_OUTGOING | typeof REL_TYPE_INCOMING
    },
    abortController?: AbortController
  ) {
    const _query = generateQueryWithParameters(query, parameterValues)
    const result: QueryResponseData = {
      twins: [],
      relationships: [],
      count: 0,
      other: [],
      data: []
    }
    let page = 0
    await twinsApiService.query(
      _query,
      ({ twins: queryTwins, relationships: queryRels, other, count, data }) => {
        // delete relationships for twin ids if provided (only on first callback)
        if (page === 0 && relReplaceOptions?.twinIds) {
          const _twinIds = Array.isArray(relReplaceOptions.twinIds)
            ? relReplaceOptions.twinIds
            : [relReplaceOptions.twinIds]
          const _relType = relReplaceOptions.relTypeLoading || REL_TYPE_OUTGOING
          _twinIds.forEach((twinId) => {
            if (!relationships.value[twinId]?.[_relType]) return
            relationships.value[twinId][_relType] = relReplaceOptions.relationshipName
              ? relationships.value[twinId][_relType].filter(
                  (rel) => rel.$relationshipName !== relReplaceOptions.relationshipName
                )
              : []
          })
        }
        page++
        // Only store valid digital twins in the store to avoid polluting the store with custom query results
        const validTwins = queryTwins.filter(isDigitalTwin)
        if (validTwins.length > 0) {
          validTwins.forEach((twin) => {
            twins.value[twin.$dtId] = twin
          })
        }
        if (queryRels.length > 0) {
          queryRels.forEach((rel) => addRelationshipToState(currentState(), rel))
        }
        result.twins.push(...queryTwins)
        result.relationships.push(...queryRels)
        result.other.push(...other)
        result.data.push(...data)
        if (count) result.count += count
      },
      abortController
    )

    return result
  }

  /** Mutable view of the state refs for the relationship helper below */
  function currentState(): DigitalTwinsState {
    return { twins: twins.value, relationships: relationships.value }
  }

  /** Get incoming or outgoing relationships by provided twin ids */
  async function getRelationships(
    twinIds: string | string[],
    relationshipName?: string,
    relTypeLoading: typeof REL_TYPE_OUTGOING | typeof REL_TYPE_INCOMING = REL_TYPE_OUTGOING
  ) {
    const _twinIds = Array.isArray(twinIds) ? twinIds : [twinIds]
    // TODO: split query if more than 100 twinIds
    const query =
      `SELECT * FROM RELATIONSHIPS WHERE ${relTypeLoading === REL_TYPE_INCOMING ? '$targetId' : '$sourceId'} IN ['${_twinIds.map((_tid) => escapeCharsTwinId(_tid)).join("','")}']` +
      (relationshipName ? ` AND $relationshipName = '${relationshipName}'` : '')
    const { relationships: rels } = await queryTwins(query)
    return rels
  }

  /** Get incoming or outgoing related twins (and relationships) by provided twin */
  async function getRelatedTwins(
    twinIds: string | string[],
    relationshipName?: string,
    relTypeLoading: typeof REL_TYPE_OUTGOING | typeof REL_TYPE_INCOMING = REL_TYPE_OUTGOING
  ) {
    // TODO: split query if more than 100 twinIds
    const matchQueryPart = `(s)${relTypeLoading === REL_TYPE_INCOMING ? '<' : ''}-[r${relationshipName ? ':' + relationshipName : ''}]-${relTypeLoading === REL_TYPE_OUTGOING ? '>' : ''}(t)`
    const whereQueryPart = Array.isArray(twinIds)
      ? `s.$dtId IN ['${twinIds.map((_tid) => escapeCharsTwinId(_tid)).join("','")}']`
      : `s.$dtId = '${escapeCharsTwinId(twinIds)}'`
    const query = `SELECT r, t FROM DIGITALTWINS
      MATCH ${matchQueryPart}
      WHERE ${whereQueryPart}`
    const { twins: relTwins, relationships: rels } = await queryTwins(query, undefined, {
      twinIds,
      relationshipName,
      relTypeLoading
    })
    return { twins: relTwins, relationships: rels }
  }

  /** Get a single twin */
  async function getTwinById<T extends BasicDigitalTwin = BasicDigitalTwin>(twinId: string) {
    const twin = await twinsApiService.getTwinById<T>(twinId)
    twins.value[twinId] = twin
    return twin
  }

  /** Get multiple twins by Ids using the getTwinById API */
  async function getTwinsByIds(twinIds: string[]) {
    return (await runBatchPromises(twinIds.map((twinId) => getTwinById(twinId)))).filter(
      isDigitalTwin
    )
  }

  /** Adds or replaces a Digital Twin */
  async function addTwin<T extends BasicDigitalTwin = BasicDigitalTwin>(twin: T) {
    // Clean $ keys (except model) (make sure not to mutate the original object)
    const _twin = Object.fromEntries(
      Object.entries(twin).filter(
        ([key]) => key === '$dtId' || key === '$metadata' || !key?.startsWith('$')
      )
    ) as BasicDigitalTwin
    // Make sure that metadata for component is set
    for (const component of useModelsStore().components(_twin.$metadata.$model)) {
      if (!_twin[component.name]) {
        // Component always needs to have metadata
        _twin[component.name] = { $metadata: {} }
      } else if (
        (_twin[component.name] as object)?.constructor === Object &&
        !(_twin[component.name] as BasicDigitalTwinComponent).$metadata
      ) {
        // Somehow the component value exists as an object, but $metadata is missing
        ;(_twin[component.name] as BasicDigitalTwinComponent).$metadata = {}
      }
    }
    const twinResponse = await twinsApiService.addTwin(_twin.$dtId, _twin)
    twins.value[_twin.$dtId] = twinResponse
    return twinResponse as T
  }

  /** Adds or replaces multiple Digital Twins */
  async function addTwins(twinList: BasicDigitalTwin[]) {
    // TODO: if there are more than x operations, run batch job on backend
    return await runBatchPromises(twinList.map((twin) => addTwin(twin)))
  }

  /** Patch a Digital Twin on the server */
  async function updateTwin(twinId: string, patch: Operation[]) {
    const twin = twins.value[twinId]
    if (twin) {
      // Make sure that metadata for component is set
      for (const component of useModelsStore().components(twin.$metadata.$model)) {
        if (!twin[component.name] && !patch.some((p) => p.path.includes(component.name))) {
          patch.push({ op: 'add', path: `/${component.name}`, value: { $metadata: {} } })
        }
      }
    }
    const normalizedPatch = patch.map((op) =>
      (op.op === 'add' || op.op === 'replace') && 'value' in op && op.value === null
        ? { op: 'remove' as const, path: op.path }
        : op
    )
    patchTwinInState(twinId, normalizedPatch)
    try {
      await twinsApiService.updateTwin(twinId, normalizedPatch)
    } catch (error) {
      delete twins.value[twinId] // remove from state if patch fails
      throw error
    }
    return twins.value[twinId]
  }

  /** Patch a twin in state */
  function patchTwinInState(twinId: string, patch: Operation[]) {
    if (twins.value[twinId]) {
      try {
        applyPatch(twins.value[twinId], patch, true, true)
      } catch {
        // console.error('Failed to apply patch: ', patch, e)
      }
    }
    return twins.value[twinId]
  }

  /** Patch multiple Digital Twins (with the same patch) */
  async function updateTwins(twinIds: string[], patch: Operation[]) {
    // TODO: if there are more than x operations, run batch job on backend
    return await runBatchPromises(twinIds.map((twinId) => updateTwin(twinId, patch)))
  }

  /** Deletes a Digital Twin and its relationships */
  async function deleteTwin(twinId: string) {
    await twinsApiService.deleteTwin(twinId)
    removeTwinFromState(twinId)
  }

  /** Remove a twin from state */
  function removeTwinFromState(twinId: string) {
    delete twins.value[twinId]
    delete relationships.value[twinId]
  }

  /** Deletes multiple Digital Twins and their relationships */
  async function deleteTwins(twinIds: string[]) {
    await runBatchPromises(twinIds.map((twinId) => twinsApiService.deleteTwin(twinId)))
    twinIds.forEach((twinId) => {
      delete twins.value[twinId]
      delete relationships.value[twinId]
    })
  }

  /** Adds or updates a relationship */
  async function addRelationship(relationship: BasicRelationship) {
    const rel = await twinsApiService.addRelationship(relationship)
    addRelationshipToState(currentState(), rel)
    return rel
  }

  /** Adds or updates a relationship in the state */
  function addRelationshipToStateAction(relationship: BasicRelationship) {
    return addRelationshipToState(currentState(), relationship)
  }

  /** Add multiple relationships */
  async function addRelationships(relationshipList: BasicRelationship[]) {
    const rels = await runBatchPromises(
      relationshipList.map((rel) => twinsApiService.addRelationship(rel))
    )
    relationshipList.forEach((rel) => addRelationshipToState(currentState(), rel))
    return rels
  }

  /** Patch a relationship */
  async function updateRelationship(twinId: string, relationshipId: string, patch: Operation[]) {
    await twinsApiService.updateRelationship(twinId, relationshipId, patch)
    return patchRelationshipInState(twinId, relationshipId, patch)
  }

  /** Patch a relationship in state */
  function patchRelationshipInState(twinId: string, relationshipId: string, patch: Operation[]) {
    const sourceRelIdx = relationships.value[twinId][REL_TYPE_OUTGOING].findIndex(
      (rel) => rel.$relationshipId === relationshipId
    )
    const relationship = relationships.value[twinId][REL_TYPE_OUTGOING][sourceRelIdx]
    if (sourceRelIdx > -1) {
      try {
        applyPatch(relationships.value[twinId][REL_TYPE_OUTGOING][sourceRelIdx], patch, true, true)
        const targetRelIdx = relationships.value[relationship.$targetId][
          REL_TYPE_INCOMING
        ].findIndex(
          (rel) =>
            rel.$relationshipId === relationshipId && rel.$sourceId === relationship.$sourceId
        )
        if (targetRelIdx > -1) {
          applyPatch(
            relationships.value[relationship.$targetId][REL_TYPE_INCOMING][targetRelIdx],
            patch,
            true,
            true
          )
        }
      } catch {
        // console.error('Failed to apply patch: ', patch, e)
      }
    }
    return relationships.value[twinId][REL_TYPE_OUTGOING][sourceRelIdx]
  }

  /** Deletes a relationship */
  async function deleteRelationship(relationship: BasicRelationship) {
    await twinsApiService.deleteRelationship(relationship.$sourceId, relationship.$relationshipId)
    removeRelationshipFromState(relationship)
  }

  function removeRelationshipFromState({
    $sourceId,
    $relationshipId,
    $targetId
  }: BasicRelationship) {
    const sourceRelIdx = relationships.value[$sourceId]?.[REL_TYPE_OUTGOING].findIndex(
      (rel) => rel.$relationshipId === $relationshipId
    )
    if (sourceRelIdx > -1) relationships.value[$sourceId][REL_TYPE_OUTGOING].splice(sourceRelIdx, 1)
    const targetRelIdx = relationships.value[$targetId]?.[REL_TYPE_INCOMING].findIndex(
      (rel) => rel.$relationshipId === $relationshipId && rel.$sourceId === $sourceId
    )
    if (targetRelIdx > -1) relationships.value[$targetId][REL_TYPE_INCOMING].splice(targetRelIdx, 1)
  }

  /** Delete multiple relationships */
  async function deleteRelationships(relationshipList: BasicRelationship[]) {
    await runBatchPromises(relationshipList.map((rel) => deleteRelationship(rel)))
  }

  /** Run batch job and show progress notification */
  async function runBatchJob(
    job: DigitalTwinsFile | BatchFile,
    update?: (customStatus?: BatchStatus) => void,
    done?: (output?: BatchOrchestrationResult) => void
  ): Promise<BatchOrchestrationResult | undefined> {
    const notify = Notify.create({
      group: false,
      timeout: 0,
      // spinner: true,
      position: 'bottom-right',
      message: 'Processing request...'
    })
    try {
      return await twinsBatchApiService.runTwinsBatchJob(
        job,
        (customStatus) => {
          if (!customStatus) return
          notify({
            // spinner: false,
            message: 'Processing request...',
            caption:
              '( ' +
              (customStatus.done || 0) +
              ' done ' +
              (customStatus.failed || 0) +
              ' failed ) / ' +
              (customStatus.total || 0) +
              ' total'
          })
          if (typeof update === 'function') update(customStatus)
        },
        (output) => {
          processBatchJob(job)
          notify({
            icon: !output || output.failed > 0 ? tabOutlineAlertCircle : tabOutlineCheck, // we add an icon
            // spinner: false,
            message: 'Processing done!',
            ...(output && {
              caption: (output.done || 0) + ' done ' + (output.failed || 0) + ' failed'
            }),
            // TODO: maybe show exceptions
            timeout: output && output.failed > 0 ? 5000 : 2000 // autoclose
          })
          if (typeof done === 'function') done(output)
        }
      )
    } catch (e) {
      const message =
        e instanceof AxiosError ? e.response?.data?.error || e.response?.data : (e as Error).message
      notify({
        icon: tabOutlineAlertCircle,
        message: 'Processing error!',
        caption: message,
        timeout: 5000
      })
      console.error(e)
    }
  }

  /** Applies a batch job to the store */
  function processBatchJob(
    job: DigitalTwinsFile | BatchFile,
    jobResult?: BatchOrchestrationResult
  ) {
    const modelsStore = useModelsStore()

    const deleteTwins = (job as BatchFile).delete?.digitaltwins || []
    deleteTwins
      .filter(
        (twinId) => !jobResult?.delete?.digitaltwins?.exceptions?.find((x) => x.id === twinId)
      )
      .forEach((twinId) => {
        delete twins.value[twinId]
        delete relationships.value[twinId]
      })
    const deleteRelationships = (job as BatchFile).delete?.relationships || []
    deleteRelationships
      .filter(
        (rel) =>
          !jobResult?.delete?.relationships?.exceptions?.find(
            (x) => x.id === rel.$sourceId && x.relationshipId === rel.$relationshipId
          )
      )
      .forEach((relationship) => {
        const sourceRelIdx = relationships.value[relationship.$sourceId][
          REL_TYPE_OUTGOING
        ].findIndex((rel) => rel.$relationshipId === relationship.$relationshipId)
        if (sourceRelIdx > -1)
          relationships.value[relationship.$sourceId][REL_TYPE_OUTGOING].splice(sourceRelIdx, 1)
        const targetRelIdx = relationships.value[relationship.$targetId][
          REL_TYPE_INCOMING
        ].findIndex((rel) => rel.$relationshipId === relationship.$relationshipId)
        if (targetRelIdx > -1)
          relationships.value[relationship.$targetId][REL_TYPE_INCOMING].splice(targetRelIdx, 1)
      })

    const addTwins = [
      ...((job as BatchFile).put?.digitaltwins || []),
      ...((job as DigitalTwinsFile).digitalTwinsGraph?.digitalTwins || [])
    ]
    addTwins
      .filter((twin) => !jobResult?.put?.digitaltwins?.exceptions?.find((x) => x.id === twin.$dtId))
      .forEach((twin) => {
        twins.value[twin.$dtId] = twin
      })

    const addRels = [
      ...((job as BatchFile).put?.relationships || []),
      ...((job as DigitalTwinsFile).digitalTwinsGraph?.relationships || [])
    ]
    addRels
      .filter(
        (rel) =>
          !jobResult?.put?.relationships?.exceptions?.find(
            (x) => x.id === rel.$sourceId && x.relationshipId === rel.$relationshipId
          )
      )
      .forEach((rel) => {
        addRelationshipToState(currentState(), rel)
      })

    const patchTwins = (job as BatchFile).patch?.digitaltwins || []
    patchTwins
      .filter(
        ({ twinId }) => !jobResult?.patch?.digitaltwins?.exceptions?.find((x) => x.id === twinId)
      )
      .forEach(({ twinId, patch }) => {
        try {
          if (twins.value[twinId]) applyPatch(twins.value[twinId], patch, true, true)
        } catch {
          // console.error('Failed to apply patch: ', patch, e)
        }
      })

    const addModels = [
      ...((job as BatchFile).models || []),
      ...((job as DigitalTwinsFile).digitalTwinsModels || [])
    ]
      .filter((model) => !jobResult?.models?.exceptions?.find((x) => x.id === model['@id']))
      .filter((model) => !modelsStore.models.find((m) => m.id === model['@id']))
      .map((m) => ({
        id: m['@id'],
        ...(m.displayName && {
          displayName: typeof m.displayName === 'string' ? { en: m.displayName } : m.displayName
        }),
        ...(m.description && {
          description: typeof m.description === 'string' ? { en: m.description } : m.description
        }),
        model: m
      }))

    if (addModels.length > 0) {
      setTimeout(async () => {
        modelsStore.models = await processModels([
          ...modelsStore.models.filter((m) => !addModels?.some((x) => x.id === m.id)),
          ...addModels
        ])
      })
    }
  }

  /** Run import job and show progress notification */
  async function runImportJob(
    jobId: string,
    inputBlobUri: string,
    outputBlobUri?: string,
    update?: (status?: ImportJobWithStatus) => void,
    done?: (job?: ImportJob) => void
  ): Promise<ImportJob> {
    const notify = Notify.create({
      group: false,
      timeout: 0,
      // spinner: true,
      position: 'bottom-right',
      message: 'Starting import...'
    })
    try {
      return await twinsJobsApiService.runImportJob(
        jobId,
        inputBlobUri,
        outputBlobUri,
        (status) => {
          if (!status) return
          notify({
            // spinner: false,
            message: 'Import in progress...',
            caption:
              '( ' +
              ((status.modelsCreated || 0) +
                (status.twinsCreated || 0) +
                (status.relationshipsCreated || 0)) +
              ' completed ' +
              (status.errorCount || 0) +
              ' errors )'
          })
          if (typeof update === 'function') update(status)
        },
        (job) => {
          notify({
            icon: !job || job.status === 'failed' ? tabOutlineAlertCircle : tabOutlineCheck,
            // spinner: false,
            message: 'Import done!',
            caption:
              job?.status === 'succeeded'
                ? 'Import completed successfully'
                : job?.status === 'partiallySucceeded'
                  ? 'Import partially succeeded'
                  : 'Import failed',
            timeout: job?.status === 'failed' ? 5000 : 2000
          })
          if (typeof done === 'function') done(job)
        }
      )
    } catch (e) {
      const message =
        e instanceof AxiosError ? e.response?.data?.error || e.response?.data : (e as Error).message
      notify({
        icon: tabOutlineAlertCircle,
        message: 'Import error!',
        caption: message,
        timeout: 5000
      })
      console.error(e)
      throw e
    }
  }

  return {
    twins,
    relationships,
    allTwins,
    allRels,
    twinsByIds,
    relatedTwins,
    twinsByRelationship,
    twinsByModel,
    pathsById,
    queryTwins,
    getRelationships,
    getRelatedTwins,
    getTwinById,
    getTwinsByIds,
    addTwin,
    addTwins,
    updateTwin,
    patchTwinInState,
    updateTwins,
    deleteTwin,
    removeTwinFromState,
    deleteTwins,
    addRelationship,
    addRelationshipToState: addRelationshipToStateAction,
    addRelationships,
    updateRelationship,
    patchRelationshipInState,
    deleteRelationship,
    removeRelationshipFromState,
    deleteRelationships,
    runBatchJob,
    processBatchJob,
    runImportJob
  }
})

/** Helper method to add a new/updated relationship to the state */
const addRelationshipToState = (state: DigitalTwinsState, rel: BasicRelationship) => {
  // Add outgoing relationship
  if (!state.relationships[rel.$sourceId]) {
    state.relationships[rel.$sourceId] = {
      [REL_TYPE_INCOMING]: [],
      [REL_TYPE_OUTGOING]: [rel]
    }
  } else {
    const relIdx = state.relationships[rel.$sourceId][REL_TYPE_OUTGOING].findIndex(
      (x) => x.$relationshipId === rel.$relationshipId
    )
    if (relIdx > -1) state.relationships[rel.$sourceId][REL_TYPE_OUTGOING].splice(relIdx, 1)
    state.relationships[rel.$sourceId][REL_TYPE_OUTGOING].push(rel)
  }
  // Add incoming relationship
  if (!state.relationships[rel.$targetId]) {
    state.relationships[rel.$targetId] = {
      [REL_TYPE_INCOMING]: [rel],
      [REL_TYPE_OUTGOING]: []
    }
  } else {
    const relIdx = state.relationships[rel.$targetId][REL_TYPE_INCOMING].findIndex(
      (x) => x.$relationshipId === rel.$relationshipId && x.$sourceId === rel.$sourceId
    )
    if (relIdx > -1) state.relationships[rel.$targetId][REL_TYPE_INCOMING].splice(relIdx, 1)
    state.relationships[rel.$targetId][REL_TYPE_INCOMING].push(rel)
  }
}
